import {
  DoubleMaterialityIroTypeEnum,
  QuestionDto,
} from "@green0meter/api-client";

export const IRO_VALUES: readonly DoubleMaterialityIroTypeEnum[] = [
  DoubleMaterialityIroTypeEnum.Impact,
  DoubleMaterialityIroTypeEnum.Risk,
  DoubleMaterialityIroTypeEnum.Opportunity,
] as const;
export const IRO_LABEL_MAP: Record<DoubleMaterialityIroTypeEnum, string> = {
  [DoubleMaterialityIroTypeEnum.Impact]: "Impact",
  [DoubleMaterialityIroTypeEnum.Risk]: "Risks",
  [DoubleMaterialityIroTypeEnum.Opportunity]: "Opportunities",
};
export const IRO_CHIP_COLORS: Record<
  DoubleMaterialityIroTypeEnum, { background: string; border: string; text: string; }
> = {
  [DoubleMaterialityIroTypeEnum.Impact]: {
    background: "#E3F2FD",
    border: "#90CAF9",
    text: "#1565C0",
  },
  [DoubleMaterialityIroTypeEnum.Risk]: {
    background: "#F5E6B3",
    border: "#F3C186",
    text: "#DE8217",
  },
  [DoubleMaterialityIroTypeEnum.Opportunity]: {
    background: "#E6F5ED",
    border: "#C4E5D2",
    text: "#2B8B58",
  },
};



export type QuestionIroApiValue = QuestionDto["iro"];

type IroInput =
  | DoubleMaterialityIroTypeEnum
  | number
  | string
  | null
  | undefined;

const mapIroValueInternal = (
  value: IroInput,
): DoubleMaterialityIroTypeEnum | undefined => {
  if (typeof value === "number") {
    switch (value) {
      case DoubleMaterialityIroTypeEnum.Impact:
      case DoubleMaterialityIroTypeEnum.Risk:
      case DoubleMaterialityIroTypeEnum.Opportunity:
        return value;
      default:
        return undefined;
    }
  }

  if (typeof value === "string") {
    const normalized = value.toLowerCase();

    switch (normalized) {
      case "0":
      case "impact":
        return DoubleMaterialityIroTypeEnum.Impact;
      case "1":
      case "risk":
        return DoubleMaterialityIroTypeEnum.Risk;
      case "2":
      case "opportunity":
        return DoubleMaterialityIroTypeEnum.Opportunity;
      default:
        return undefined;
    }
  }

  return undefined;
};

const normalizeIroValues = (
  value: IroInput | IroInput[],
  { allowEmpty = false }: { allowEmpty?: boolean } = {},
): DoubleMaterialityIroTypeEnum[] => {
  const rawValues = Array.isArray(value) ? value : [value];

  const mapped = rawValues
    .map((item) => mapIroValueInternal(item))
    .filter(
      (item): item is DoubleMaterialityIroTypeEnum => item !== undefined,
    );

  if (!allowEmpty && mapped.length === 0) {
    return [DoubleMaterialityIroTypeEnum.Impact];
  }

  return mapped;
};

export const mapIroValue = (
  value: QuestionIroApiValue | QuestionIroApiValue[] | unknown,
): DoubleMaterialityIroTypeEnum | undefined => {
  if (Array.isArray(value)) {
    const first = value[0];

    return first !== undefined
      ? mapIroValueInternal(first as IroInput)
      : undefined;
  }

  return mapIroValueInternal(value as IroInput);
};

export const normalizeIro = (
  iro: QuestionIroApiValue | QuestionIroApiValue[] | null | undefined,
): DoubleMaterialityIroTypeEnum[] => {
  return normalizeIroValues(iro as IroInput | IroInput[], {
    allowEmpty: false,
  });
};
